import { useEffect, useRef } from 'react';
import type { FC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import {
  getAssetMainImage,
  getAssetPreviewUrl,
} from '../../shared/helpers/assetHelpers';
import { Image } from '../Image/Image';
import { getImageSources } from '../../helpers/images';
import { getStoryUrl } from '../../shared/helpers/routes';

import * as styles from './Story.module.scss';

interface StoryProps {
  asset: SvpAssetAdditional;
  index: number;
  isPreviewEnabled?: boolean;
  onClick?: () => void;
  onIntersectionChange?: (
    entry: IntersectionObserverEntry,
    index: number
  ) => void;
}

export const Story: FC<StoryProps> = ({
  asset,
  index,
  isPreviewEnabled,
  onClick,
  onIntersectionChange,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null!);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        onIntersectionChange?.(entry, index);
      },
      { threshold: 1 }
    );

    observer.observe(videoRef.current);

    return () => observer.disconnect();
  }, [onIntersectionChange, index]);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const video = videoRef.current;

    if (isPreviewEnabled) {
      video.play();
    } else {
      video.pause();
    }

    return () => video.pause();
  }, [isPreviewEnabled]);

  const previewUrl = getAssetPreviewUrl(asset);

  return (
    <a
      href={getStoryUrl(asset.id)}
      className={styles.thumbnail}
      onClick={onClick}
    >
      <Image
        className={styles.image}
        altText={asset.title}
        imageSources={getImageSources({
          imageUrl: getAssetMainImage(asset),
          aspectRatio: '9/16',
          imagePreset: 'assetsSliderVertical',
        })}
      />
      {previewUrl && (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <video
          src={previewUrl}
          className={styles.preview}
          ref={videoRef}
          controls={false}
          playsInline
          preload="none"
          disableRemotePlayback
          disablePictureInPicture
          x-webkit-airplay="deny"
          x-webkit-wirelessvideoplaybackdisabled="true"
          muted
          loop
          onMouseOver={() => videoRef.current.play()}
          onMouseOut={() => videoRef.current.pause()}
        />
      )}
      <div className={styles.metadata}>
        <h3 className={styles.title}>{asset.title}</h3>
      </div>
    </a>
  );
};
